import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { DataModeSelector } from '@/components/ui/data-mode-selector';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { CompanySelector } from '@/components/company-selector';
import { SensitiveDataToggle } from '@/components/sensitive-data';
import { SidebarProvider, SidebarInset } from '@/components/blocks/evo-sidebar';
import { LeftMenu } from '@/components/LeftMenu';
import { useKeyboardShortcuts, type KeyboardShortcut } from '@/hooks/use-keyboard-shortcuts';
import { CommandPalette } from '@/components/command-palette';
import { KeyboardShortcutsDialog } from '@/components/keyboard-shortcuts-dialog';
import { shouldShowSensitiveDataToggle } from '@/config/sensitive-data-pages';
import { ROUTES } from '@/routes';

interface BreadcrumbItem {
  label: string;
  href?: string;
  isCurrentPage?: boolean;
  isHome?: boolean;
}

interface AppLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  breadcrumbItems?: BreadcrumbItem[];
}

const navigation = [
  { id: 'dashboard', name: 'Homepage', route: ROUTES.DASHBOARD },
  { id: 'projects', name: 'Projects', route: ROUTES.PROJECTS },
  { id: 'portfolio', name: 'Portfolio', route: ROUTES.PORTFOLIO },
  { id: 'utilisation', name: 'Utilisation', route: ROUTES.UTILISATION },
  { id: 'wip-reporting', name: 'WIP', route: ROUTES.WIP_REPORTING },
  { id: 'profitability', name: 'Profitability', route: ROUTES.PROFITABILITY },
  { id: 'budgets', name: 'Budgets & Variance', route: ROUTES.BUDGETS },
  { id: 'resources', name: 'Resource Allocation', route: ROUTES.RESOURCES },
  { id: 'project-transactions', name: 'Project Transactions', route: ROUTES.PROJECT_TRANSACTIONS },
  { id: 'gantt', name: 'Gantt Charts', route: ROUTES.GANTT },
  { id: 'settings', name: 'Settings', route: ROUTES.SETTINGS },
];

export function AppLayout({ children, currentPage, breadcrumbItems = [] }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);

  // Determine active page from route
  const activePage = location.pathname.slice(1) || 'dashboard';

  // Get the display name for the current page to use as the feedback feature
  const currentPageName = navigation.find(item => item.id === currentPage)?.name ?? 'Projects';

  // Handle page change from LeftMenu
  const handlePageChange = (page: string) => {
    // Map page names to routes
    const routeMap: Record<string, string> = {
      'dashboard': ROUTES.DASHBOARD,
      'projects': ROUTES.PROJECTS,
      'portfolio': ROUTES.PORTFOLIO,
      'utilisation': ROUTES.UTILISATION,
      'wip-reporting': ROUTES.WIP_REPORTING,
      'profitability': ROUTES.PROFITABILITY,
      'budgets': ROUTES.BUDGETS,
      'resources': ROUTES.RESOURCES,
      'directory': ROUTES.DIRECTORY,
      'roles-rates': ROUTES.ROLES_RATES,
      'gantt': ROUTES.GANTT,
      'project-transactions': ROUTES.PROJECT_TRANSACTIONS,
      'settings-general': ROUTES.SETTINGS_GENERAL,
      'settings-project': ROUTES.SETTINGS_PROJECT,
      'settings-user-preferences': ROUTES.SETTINGS_USER_PREFERENCES,
      'settings-integrations': ROUTES.SETTINGS_INTEGRATIONS,
      'settings-feature-labs': ROUTES.SETTINGS_FEATURE_LABS,
      'settings-user-permissions': ROUTES.SETTINGS_USER_PERMISSIONS,
      'settings-templates': ROUTES.SETTINGS_TEMPLATES,
      'project-templates': ROUTES.PROJECT_TEMPLATES,
    };
    
    const route = routeMap[page] || `/${page}`;
    navigate(route);
  };

  // Define keyboard shortcuts
  const shortcuts: KeyboardShortcut[] = [
    // Navigation shortcuts (Cmd/Ctrl + 1-9)
    {
      key: '1',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.DASHBOARD),
      description: 'Go to Homepage',
      category: 'navigation',
    },
    {
      key: '2',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PROJECTS),
      description: 'Go to Projects',
      category: 'navigation',
    },
    {
      key: '3',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PORTFOLIO),
      description: 'Go to Portfolio',
      category: 'navigation',
    },
    {
      key: '4',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.UTILISATION),
      description: 'Go to Utilisation',
      category: 'navigation',
    },
    {
      key: '5',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.WIP_REPORTING),
      description: 'Go to WIP',
      category: 'navigation',
    },
    {
      key: '6',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PROFITABILITY),
      description: 'Go to Profitability',
      category: 'navigation',
    },
    {
      key: '7',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.BUDGETS),
      description: 'Go to Budgets & Variance',
      category: 'navigation',
    },
    {
      key: '8',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.RESOURCES),
      description: 'Go to Resource Allocation',
      category: 'navigation',
    },
    {
      key: '9',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.GANTT),
      description: 'Go to Gantt Charts',
      category: 'navigation',
    },
    // Command palette (Cmd/Ctrl + K)
    {
      key: 'k',
      ctrlKey: true,
      metaKey: true,
      action: () => setCommandPaletteOpen(true),
      description: 'Open command palette',
      category: 'actions',
    },
    // Settings (Cmd/Ctrl + ,)
    {
      key: ',',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.SETTINGS),
      description: 'Open Settings',
      category: 'actions',
    },
    // Keyboard shortcuts help (Cmd/Ctrl + /)
    {
      key: '/',
      ctrlKey: true,
      metaKey: true,
      action: () => setShortcutsDialogOpen(true),
      description: 'Show keyboard shortcuts',
      category: 'actions',
    },
  ];

  // Register keyboard shortcuts
  useKeyboardShortcuts({ shortcuts });

  // Prepare shortcuts for the help dialog
  const shortcutCategories = [
    {
      title: 'Navigation',
      shortcuts: [
        { key: '1', ctrlKey: true, metaKey: true, description: 'Go to Homepage' },
        { key: '2', ctrlKey: true, metaKey: true, description: 'Go to Projects' },
        { key: '3', ctrlKey: true, metaKey: true, description: 'Go to Portfolio' },
        { key: '4', ctrlKey: true, metaKey: true, description: 'Go to Utilisation' },
        { key: '5', ctrlKey: true, metaKey: true, description: 'Go to WIP' },
        { key: '6', ctrlKey: true, metaKey: true, description: 'Go to Profitability' },
        { key: '7', ctrlKey: true, metaKey: true, description: 'Go to Budgets & Variance' },
        { key: '8', ctrlKey: true, metaKey: true, description: 'Go to Resource Allocation' },
        { key: '9', ctrlKey: true, metaKey: true, description: 'Go to Gantt Charts' },
      ],
    },
    {
      title: 'Actions',
      shortcuts: [
        { key: 'k', ctrlKey: true, metaKey: true, description: 'Open command palette' },
        { key: ',', ctrlKey: true, metaKey: true, description: 'Open Settings' },
        { key: '/', ctrlKey: true, metaKey: true, description: 'Show keyboard shortcuts' },
      ],
    },
    {
      title: 'User Interface',
      shortcuts: [
        { key: 'b', ctrlKey: true, metaKey: true, description: 'Toggle sidebar' },
        { key: 'Escape', description: 'Close dialogs and modals' },
      ],
    },
  ];

  return (
    <div className="flex h-screen flex-col bg-neutral-50 overflow-hidden">
      {/* Sidebar + Content Area */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <SidebarProvider defaultOpen={true}>
          <LeftMenu activePage={activePage} onPageChange={handlePageChange} />
          
          <SidebarInset className="flex flex-col min-h-0 overflow-hidden">
            {/* App Runner-Bar - Fixed Top Bar */}
            <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-6 z-50">
              <div className="flex items-center gap-3">
                {/* Company Selector */}
                <CompanySelector />
                
                {/* Breadcrumb */}
                {breadcrumbItems.length > 0 && (
                  <nav className="flex items-center gap-2 text-sm text-muted-foreground ml-4">
                    {breadcrumbItems.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        {index > 0 && <ChevronRight className="h-4 w-4" />}
                        {item.isCurrentPage ? (
                          <span className="text-foreground font-medium flex items-center gap-1.5">
                            {item.isHome && <Home className="h-4 w-4" />}
                            {item.label}
                          </span>
                        ) : item.href ? (
                          <Link to={item.href} className="hover:text-foreground transition-colors flex items-center gap-1.5">
                            {item.isHome && <Home className="h-4 w-4" />}
                            {item.label}
                          </Link>
                        ) : (
                          <span className="flex items-center gap-1.5">
                            {item.isHome && <Home className="h-4 w-4" />}
                            {item.label}
                          </span>
                        )}
                      </div>
                    ))}
                  </nav>
                )}
              </div>

              <div className="flex items-center gap-3">
                {/* Demo/Live Mode Toggle */}
                <DataModeSelector />
                
                {/* Feature Feedback Collector (thumbs up/down) */}
                <EvoFeatureFeedbackCollector feature={currentPageName} />
                
                {/* Hide Sensitive Data Toggle */}
                {shouldShowSensitiveDataToggle(currentPage) && <SensitiveDataToggle />}
              </div>
            </header>
            
            <main className="flex-1 overflow-y-auto overflow-x-hidden bg-neutral-50">
              <div className="w-full p-6">
                {children}
              </div>
            </main>
          </SidebarInset>
        </SidebarProvider>
      </div>

      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onNavigate={(page) => navigate(`/${page}`)}
      />

      {/* Keyboard Shortcuts Dialog */}
      <KeyboardShortcutsDialog
        open={shortcutsDialogOpen}
        onOpenChange={setShortcutsDialogOpen}
        categories={shortcutCategories}
      />
    </div>
  );
}
